<template>
  <base-modal-ex :active="true"
    :can-cancel="false"
    has-modal-card
    :min-width="42"
    :max-height="90"
    role="dialog"
    aria-modal="true">
    <header class="modal-card-head">
      <span class="title-badge has-background-primary mr-4">
        <i class="mdi mdi-24px mdi-file-document-outline" />
      </span>
      <p class="modal-card-title">Terms &amp; Conditions Update</p>
    </header>

    <section class="modal-card-body">
      <p class="mb-4">Please take a moment to review what&rsquo;s changed below.</p>

      <p v-if="loadError"
        class="help is-danger mb-3">{{ loadError }}</p>

      <div v-if="isLoading"
        class="terms-viewport is-flex is-flex-direction-column is-justify-content-center
          has-text-centered has-text-grey">
        <i class="mdi mdi-24px mdi-loading mdi-spin" />
        <p class="mt-2">Loading the terms&hellip;</p>
      </div>

      <!-- This element is the gate, so it has to be the one that scrolls, not the modal card. -->
      <div v-else
        ref="scroller"
        class="terms-viewport terms-content content"
        tabindex="0"
        @scroll="evaluateScroll"
        v-html="content" />

      <div class="terms-meta is-flex is-justify-content-space-between is-align-items-baseline mt-3">
        <span v-if="terms"
          class="is-size-7 has-text-grey">
          Version {{ terms.versionNumber }} &middot; Effective {{ effectiveDate }}
        </span>
        <span v-else />

        <!-- rel is set because target="_blank" otherwise hands the new tab a window.opener back
             to the login page. -->
        <a v-if="terms && terms.contentUrl"
          :href="terms.contentUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="is-size-7 has-text-primary has-text-weight-medium">
          Download full PDF
          <span class="icon is-small"><i class="mdi mdi-arrow-down" /></span>
        </a>
      </div>
    </section>

    <footer class="modal-card-foot is-flex-direction-column is-align-items-stretch">
      <p v-if="submitError"
        class="help is-danger has-text-right mb-2"
        role="alert">{{ submitError }}</p>

      <!-- Always rendered, never v-if: the line reserves its own height, so the modal does not
           resize when the hint clears. -->
      <p class="accept-hint is-size-7 has-text-grey has-text-right mb-2">
        {{ acceptBlockedReason }}
      </p>

      <div class="is-flex is-justify-content-flex-end is-align-items-center">
        <!-- Enabled at all times, independent of scroll position. The icon is always present and
             only swaps glyph, so starting the spinner cannot change the button's width. -->
        <button type="button"
          class="button mr-3"
          :disabled="isBusy"
          @click="$emit('cancel')">
          <span class="icon mr-1">
            <i class="mdi mdi-18px"
              :class="busyAction === 'cancel' ? 'mdi-loading mdi-spin' : 'mdi-arrow-left'" />
          </span>
          Cancel and log out
        </button>

        <!-- title repeats the reason, so hovering the disabled button explains itself to anyone
             who missed the line above it. -->
        <button type="button"
          class="button is-success accept-button"
          :disabled="!canAccept"
          :title="acceptBlockedReason"
          @click="$emit('accept')">
          <span class="icon mr-1">
            <i class="mdi mdi-18px"
              :class="busyAction === 'accept' ? 'mdi-loading mdi-spin' : 'mdi-check'" />
          </span>
          Accept &amp; Continue
        </button>
      </div>

      <p class="is-size-7 has-text-grey mt-4 mb-0">
        Recorded against <strong class="has-text-grey-light">your user account</strong> with
        today&rsquo;s date and time for audit purposes.
      </p>
    </footer>
  </base-modal-ex>
</template>

<script>
import BaseModalEx from '@/components/BulmaModal/BaseModalEx.vue'
// Luxon directly rather than @/utils/DateFunctions, which imports @/store and pulls the router,
// NavService and the whole API layer in behind it. This component renders on the login page
// before any of that exists, and the format below is the one formatDateLong uses.
import { DateTime } from 'luxon'

// Fractional element heights and non-integer device pixel ratios mean scrollTop + clientHeight
// lands a pixel or two short of scrollHeight at the bottom. Without this tolerance the button
// never enables on some displays.
const SCROLL_TOLERANCE_PX = 2

export default {
  name: 'TermsAcceptanceModal',
  components: { BaseModalEx },
  props: {
    /** From the sign-in response: { id, versionNumber, effectiveFromUtc, contentUrl }. */
    terms: {
      type: Object,
      default: null
    },
    /**
     * Which button is currently working: 'accept', 'cancel', or null. Named rather than a plain
     * boolean so the spinner lands on the button that was actually pressed - one shared flag put
     * it on both.
     */
    busyAction: {
      type: String,
      default: null,
      validator: v => v === null || v === 'accept' || v === 'cancel'
    },
    /**
     * A failed acceptance, shown inside the modal. The login page's own error alert sits behind
     * the overlay, so anything reported there while this is open is invisible.
     */
    submitError: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      content: '',
      isLoading: true,
      loadError: '',
      // Latched, never cleared: once read, resizing or zooming must not take the button away.
      hasReadToEnd: false
    }
  },
  computed: {
    isBusy() {
      return !!this.busyAction
    },
    /**
     * Whether the terms have been read and are acceptable, ignoring any request already in
     * flight. Kept separate from canAccept so the hint below does not blink out and back while a
     * button is working - being busy is not a reason the terms are unread.
     *
     * `content` and `loadError` matter here, not just `hasReadToEnd`: an empty scroll container
     * reports scrollHeight === clientHeight === 0, which satisfies the scrolled-to-the-end test.
     * Without them a failed fetch would enable the button over a document that never rendered.
     */
    isReadyToAccept() {
      return this.hasReadToEnd && !!this.content && !this.loadError && !this.isLoading
    },
    canAccept() {
      return this.isReadyToAccept && !this.isBusy
    },
    /**
     * Why Accept & Continue is disabled, or null when it is not. A disabled button with no
     * explanation is the worst case here - the user cannot proceed and cannot tell why.
     *
     * Null rather than an empty string: Vue drops the title attribute entirely for null, where
     * '' would leave an empty tooltip behind.
     */
    acceptBlockedReason() {
      if (this.isReadyToAccept) return null
      if (this.isLoading) return 'The terms are still loading'
      if (this.loadError) return 'The terms could not be loaded'
      return 'Scroll to the end to continue'
    },
    effectiveDate() {
      if (!this.terms) return ''
      // EffectiveFromUtc is an EF DateTime with Kind=Unspecified, so it serialises without a Z
      // and would otherwise be read in the browser's zone - showing the wrong day to anyone west
      // of UTC, against a value the server compares in UTC.
      return DateTime.fromISO(this.terms.effectiveFromUtc, { zone: 'utc' })
        .toFormat('ccc, d MMM yyyy')
    }
  },
  async mounted() {
    // A resize can turn a scrolling document into a non-scrolling one, which fires no scroll
    // event - so the gate has to be re-evaluated on resize as well as on scroll.
    window.addEventListener('resize', this.evaluateScroll)

    try {
      const current = await this.$auth.terms.getCurrent()

      // The body comes from here, but the id being accepted came from the sign-in response. If a
      // new version became effective in between the user would read one document and accept
      // another. The server rejects that pairing; this stops the modal offering it.
      if (current && this.terms && current.id !== this.terms.id) {
        this.loadError = 'The Terms & Conditions have been updated. Please sign in again to review them.'
      } else {
        this.content = (current && current.content) || ''
        if (!this.content) {
          this.loadError = 'The Terms & Conditions could not be loaded. Please try signing in again.'
        }
      }
    } catch (e) {
      this.loadError = 'The Terms & Conditions could not be loaded. Please try signing in again.'
    } finally {
      this.isLoading = false
    }

    // Terms short enough not to scroll fire no scroll event at all, so the gate is evaluated
    // once after render or the button would stay disabled forever.
    await this.$nextTick()
    this.evaluateScroll()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.evaluateScroll)
  },
  methods: {
    evaluateScroll() {
      if (this.hasReadToEnd) return

      // Nothing rendered means nothing was read. An empty container measures as fully scrolled,
      // so without this the gate would latch open on a failed load.
      if (!this.content || this.loadError) return

      const el = this.$refs.scroller
      if (!el) return

      if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_TOLERANCE_PX) {
        this.hasReadToEnd = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card-head {
  padding: 1.5rem;
}
.modal-card-title {
  margin-bottom: 0;
  font-size: 1.35rem;
  font-weight: 700;
  flex-shrink: 1;
}
// The blue rounded tile behind the document icon. has-background-primary supplies the colour, so
// this only has to do the shape.
.title-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.65rem;
  color: #fff;
}

.modal-card-body {
  padding: 1.5rem;
}
// A fixed height, not a max-height, and shared with the loading placeholder: the modal must not
// resize between loading and loaded, or between short and long terms.
.terms-viewport {
  height: 17rem;
  border: 1px solid rgba(127, 127, 127, 0.35);
  border-radius: 6px;
}
.terms-content {
  overflow-y: auto;
  padding: 1rem 1.25rem;
  // Always-visible scrollbar: an overlay one that appears only on hover hides the affordance the
  // scroll gate depends on.
  scrollbar-gutter: stable;

  ::v-deep > :first-child {
    margin-top: 0;
  }
}
.terms-meta a {
  white-space: nowrap;
}

.modal-card-foot {
  padding: 1.25rem 1.5rem 1.5rem;
}
// Holds one line of is-size-7 whether or not there is a hint to show, so clearing the hint does
// not shrink the footer.
.accept-hint {
  min-height: 1.15rem;
}
.accept-button {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-weight: 600;
}
</style>
